if (!customElements.get('section-collections-list')) {
  customElements.define(
    'section-collections-list',
    class SectionCollectionsList extends HTMLElement {
      constructor() {
        super();
        this.flickityInstances = new Map();
        this.currentActiveTab = null;
        this.currentActiveCarousel = null;
        this.flickityConfig = null;
        this.resizeObserver = null;
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        this.destroy();
      }

      init() {
        this.parseFlickityConfig();
        this.setupTabs();
        this.setupCarousels();
        this.setupResizeObserver();
        this.activateFirstTab();
      }

      parseFlickityConfig() {
        const configAttr = this.getAttribute('data-flickity-config') || '{}';
        this.flickityConfig = JSON.parse(configAttr);
      }

      setupTabs() {
        this.tabs = this.querySelectorAll('[data-cat]');
        this.tabs.forEach((tab) => {
          tab.addEventListener('click', this.handleTabClick.bind(this));
          tab.addEventListener('keydown', this.handleTabKeydown.bind(this));
        });
      }

      setupCarousels() {
        this.carousels = this.querySelectorAll('[data-flickity-slider]');
        this.carousels.forEach((carousel) => {
          // Initially hide all carousels
          carousel.style.display = 'none';
          carousel.setAttribute('aria-hidden', 'true');
        });
      }

      setupResizeObserver() {
        if (window.ResizeObserver) {
          this.resizeObserver = new ResizeObserver(() => {
            this.handleResize();
          });
          this.resizeObserver.observe(this);
        }
      }

      activateFirstTab() {
        const firstTab = this.tabs[0];
        if (firstTab) {
          this.activateTab(firstTab);
        }
      }

      handleTabClick(event) {
        console.log('handleTabClick', event);
        event.preventDefault();
        const clickedTab = event.currentTarget;
        this.activateTab(clickedTab);
      }

      handleTabKeydown(event) {
        const tabs = Array.from(this.tabs);
        const currentIndex = tabs.indexOf(event.currentTarget);

        switch (event.key) {
          case 'ArrowLeft':
            event.preventDefault();
            const prevIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1;
            this.activateTab(tabs[prevIndex]);
            tabs[prevIndex].focus();
            break;
          case 'ArrowRight':
            event.preventDefault();
            const nextIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : 0;
            this.activateTab(tabs[nextIndex]);
            tabs[nextIndex].focus();
            break;
          case 'Enter':
          case ' ':
            event.preventDefault();
            this.activateTab(event.currentTarget);
            break;
        }
      }

      activateTab(activeTab) {
        console.log('activateTab', activeTab);
        if (this.currentActiveTab === activeTab) return console.log('activeTab is the same as the currentActiveTab');

        // Update tab states
        this.tabs.forEach((tab) => {
          const isActive = tab === activeTab;
          tab.setAttribute('aria-selected', isActive);
          tab.setAttribute('tabindex', isActive ? '0' : '-1');

          // Update visual state
          if (isActive) {
            tab.classList.add('bg-[#030509]', 'text-white');
            tab.classList.remove('text-[#475569]', 'bg-[#F1F5F9]');
          } else {
            tab.classList.remove('bg-[#030509]', 'text-white');
            tab.classList.add('text-[#475569]', 'bg-[#F1F5F9]');
          }
        });

        // Get target carousel
        const targetCarouselId = activeTab.getAttribute('href');
        const targetCarousel = this.querySelector(`${targetCarouselId}`);

        if (targetCarousel) {
          // Hide current carousel
          if (this.currentActiveCarousel) {
            this.currentActiveCarousel.style.display = 'none';
            this.currentActiveCarousel.setAttribute('aria-hidden', 'true');
            this.currentActiveCarousel.setAttribute('tabindex', '-1');
          }

          // Show target carousel
          targetCarousel.style.display = 'block';
          targetCarousel.setAttribute('aria-hidden', 'false');
          targetCarousel.setAttribute('tabindex', '0');

          // Initialize or reinitialize Flickity
          this.initializeFlickity(targetCarousel);

          // Update current references
          this.currentActiveTab = activeTab;
          this.currentActiveCarousel = targetCarousel;

          // Update View all link if needed
          this.updateViewAllLink(activeTab);

          // Emit custom event
          this.dispatchEvent(
            new CustomEvent('collection-changed', {
              detail: {
                activeTab: activeTab,
                activeCarousel: targetCarousel,
                collectionHandle: activeTab.getAttribute('data-cat'),
              },
            })
          );
        }
      }

      initializeFlickity(carousel) {
        if (!window.Flickity) {
          console.warn('Flickity is not available');
          return;
        }

        const carouselId = carousel.id;

        // Destroy existing instance if it exists
        if (this.flickityInstances.has(carouselId)) {
          this.flickityInstances.get(carouselId).destroy();
          this.flickityInstances.delete(carouselId);
        }

        // Wait for images to load and carousel to be visible
        setTimeout(() => {
          if (carousel.style.display !== 'none') {
            try {
              if (JSON.stringify(this.flickityConfig) === '{}') return;
              const flickityInstance = new Flickity(carousel, this.flickityConfig);
              if (!flickityInstance) return;
              this.flickityInstances.set(carouselId, flickityInstance);

              // Force resize after initialization
              setTimeout(() => {
                flickityInstance.resize();
              }, 100);
            } catch (error) {
              console.error('Error initializing Flickity:', error);
            }
          }
        }, 10);
      }

      updateViewAllLink(activeTab) {
        const viewAllLink = this.querySelector('[data-collection-url]');
        if (viewAllLink) {
          const targetCarouselId = activeTab.getAttribute('href').substring(1);
          const targetCarousel = this.querySelector(`#${targetCarouselId}`);

          if (targetCarousel) {
            // Extract collection URL from the carousel's data or tab's href
            const collectionHandle = activeTab.getAttribute('data-cat');
            const collectionUrl = `/collections/${collectionHandle}`;
            viewAllLink.setAttribute('href', collectionUrl);
          }
        }
      }

      handleResize() {
        // Debounce resize handling
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => {
          if (this.currentActiveCarousel) {
            const carouselId = this.currentActiveCarousel.id;
            const flickityInstance = this.flickityInstances.get(carouselId);
            if (flickityInstance) {
              flickityInstance.resize();
            }
          }
        }, 250);
      }

      destroy() {
        // Clean up resize observer
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
        }

        // Clean up timers
        if (this.resizeTimeout) {
          clearTimeout(this.resizeTimeout);
        }

        // Destroy all Flickity instances
        this.flickityInstances.forEach((instance) => {
          instance.destroy();
        });
        this.flickityInstances.clear();

        // Remove event listeners
        this.tabs.forEach((tab) => {
          tab.removeEventListener('click', this.handleTabClick.bind(this));
          tab.removeEventListener('keydown', this.handleTabKeydown.bind(this));
        });
      }
    }
  );
}
